import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ProductCard from './ProductCard';
import SectionHeader from './SectionHeader';
import useReveal from '../hooks/useReveal';

function readStoredTab(id, tabs) {
  if (!id || !tabs?.length) return tabs ? tabs[0] : null;
  try {
    const saved = sessionStorage.getItem(`carousel-tab:${id}`);
    if (saved && tabs.includes(saved)) return saved;
  } catch {
    /* ignore */
  }
  return tabs[0];
}

export default function ProductCarousel({ id, eyebrow, title, subtitle, viewAllHref = '#', tabs, products }) {
  const [tab, setTab] = useState(() => readStoredTab(id, tabs));
  const scrollerRef = useRef(null);
  const [ref, visible] = useReveal();

  const visibleProducts = useMemo(() => {
    if (!tabs || !tab) return products;
    const filtered = products.filter(p => (p.tags || []).includes(tab));
    return filtered.length ? filtered : products;
  }, [tabs, tab, products]);

  useEffect(() => {
    if (!id || !tab) return;
    try {
      sessionStorage.setItem(`carousel-tab:${id}`, tab);
    } catch {
      /* ignore */
    }
  }, [id, tab]);

  // Restore horizontal carousel position after returning from a product page
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !id) return;

    const storageKey = `carousel-scroll:${id}:${tab || 'all'}`;
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved != null) el.scrollLeft = Number(saved) || 0;
      else el.scrollLeft = 0;
    } catch {
      /* ignore */
    }

    const save = () => {
      try {
        sessionStorage.setItem(storageKey, String(el.scrollLeft));
      } catch {
        /* ignore */
      }
    };
    el.addEventListener('scroll', save, { passive: true });
    return () => {
      el.removeEventListener('scroll', save);
      save();
    };
  }, [id, tab, visibleProducts, visible]);

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: 'smooth' });
  };

  return (
    <section
      id={id}
      ref={ref}
      style={{ scrollMarginTop: '100px' }}
      className={`max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-10 sm:py-14 md:py-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6 mb-6 md:mb-8">
        <SectionHeader eyebrow={eyebrow} title={title} subtitle={subtitle} align="left" />
        <a
          href={viewAllHref}
          className="text-[11px] tracking-[0.3em] uppercase border-b border-[#1A2332] pb-1 hover:text-[#8a6f2d] hover:border-[#8a6f2d] transition self-start md:self-auto"
        >
          View All
        </a>
      </div>
      {tabs && (
        <div className="flex gap-5 md:gap-10 mb-6 md:mb-8 border-b border-[#E5DCC9] overflow-x-auto no-scrollbar -mx-3 px-3 sm:mx-0 sm:px-0">
          {tabs.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${id || 'default'}-${t.replace(/\s+/g, '-').toLowerCase()}`}
              className={`pb-3 text-xs sm:text-sm tracking-[0.15em] uppercase transition-colors relative whitespace-nowrap touch-manipulation ${tab === t ? 'text-[#1A2332]' : 'text-[#1A2332]/50 hover:text-[#1A2332]/80'}`}
            >
              {t}
              {tab === t && <span className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-[#C9A961]"></span>}
            </button>
          ))}
        </div>
      )}
      <div className="relative" data-testid={`carousel-content-${id || 'default'}`}>
        <div
          ref={scrollerRef}
          className="flex gap-3 sm:gap-4 md:gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 -mx-3 px-3 sm:mx-0 sm:px-0"
        >
          {visible
            ? visibleProducts.map(p => (
                <div
                  key={p.id}
                  className="min-w-[78vw] w-[78vw] sm:min-w-[260px] sm:w-[260px] md:min-w-[290px] md:w-[290px] snap-start"
                >
                  <ProductCard product={p} sectionId={id} />
                </div>
              ))
            : (
                <div className="min-h-[420px] w-full" aria-hidden />
              )}
        </div>
        <button
          onClick={() => scroll(-1)}
          aria-label="Prev"
          className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-lg items-center justify-center hover:scale-110 hover:bg-[#1A2332] hover:text-[#F5EFE6] transition"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => scroll(1)}
          aria-label="Next"
          className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-lg items-center justify-center hover:scale-110 hover:bg-[#1A2332] hover:text-[#F5EFE6] transition"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </section>
  );
}
